import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Album } from '../types';
import { albumsAPI, authAPI } from '../services/api';
import styled from 'styled-components';
import SearchBar from './SearchBar';
import ThemeToggle from './ThemeToggle';
import UserManagement from './UserManagement';
import LoadingSpinner from './LoadingSpinner';


const DashboardContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 2.5rem;
  font-weight: 900;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: center;
    margin-bottom: 0;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    justify-content: center;
    gap: 8px;
    
    > span {
      display: none;
    }
    
    .mobile-hidden {
      display: none;
    }
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const QuickActionCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  flex: 1;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
    background: rgba(255, 255, 255, 0.1);
  }
  
  .icon {
    font-size: 1.5rem;
  }
  
  .content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  
  .title {
    font-weight: 600;
    font-size: 0.95rem;
  }
  
  .subtitle {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    
    .icon {
      font-size: 1.2rem;
    }
    
    .title {
      font-size: 0.85rem;
    }
    
    .subtitle {
      font-size: 0.75rem;
    }
  }
`;

const AlbumsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 16px;
    margin-top: 20px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  .desktop-only {
    @media (max-width: 1024px) {
      display: none !important;
    }
  }
`;

const AlbumCard = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const AlbumCardContent = styled.div`
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.4);
    background: rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 768px) {
    border-radius: 16px;
    padding: 12px;
    
    &:hover {
      transform: none;
    }
    
    &:active {
      transform: scale(0.98);
      transition: transform 0.1s ease;
    }
  }
`;

const AlbumCover = styled.div<{ image?: string }>`
  width: 100%;
  aspect-ratio: 1;
  background: ${props => props.image 
    ? `url(${props.image})` 
    : 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))'};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  
  ${props => !props.image && `
    &::after {
      content: '🎵';
      font-size: 2.5rem;
      opacity: 0.8;
    }
  `}
`;

const StatusBadge = styled.div<{ released: boolean }>`
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => props.released 
    ? `
      background: linear-gradient(45deg, #10b981, #059669);
      color: white;
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
    `
    : `
      background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
      color: white;
      box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
    `
  }
  
  @media (max-width: 768px) {
    font-size: 9px;
    padding: 4px 8px;
  }
`;

const AlbumInfo = styled.div`
  h3 {
    margin-bottom: 4px;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  p {
    color: var(--text-muted);
    font-size: 0.85rem;
    margin-bottom: 8px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const SongCount = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-muted);
  font-size: 0.8rem;
  margin-bottom: 8px;
`;

const Modal = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--dark-bg);
  padding: 30px;
  border-radius: 15px;
  width: 90%;
  max-width: 500px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  min-height: 100px;
  resize: vertical;
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Estados do formulário de álbum
  const [albumName, setAlbumName] = useState('');
  const [albumDescription, setAlbumDescription] = useState('');
  const [albumCover, setAlbumCover] = useState<File | null>(null);

  // Estados do formulário de usuário
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userRole, setUserRole] = useState('listener');

  useEffect(() => {
    loadAlbums();
  }, []);

  useEffect(() => {
    if (editingAlbum) {
      setAlbumName(editingAlbum.name);
      setAlbumDescription(editingAlbum.description || '');
    }
  }, [editingAlbum]);

  const loadAlbums = async () => {
    try {
      const response = await albumsAPI.getAll();
      setAlbums(response.data);
    } catch (error) {
      console.error('Erro ao carregar álbuns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlbum = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    
    const formData = new FormData();
    formData.append('name', albumName);
    formData.append('description', albumDescription);
    if (albumCover) {
      formData.append('cover', albumCover);
    }

    try {
      if (editingAlbum) {
        await albumsAPI.update(editingAlbum.id, formData);
      } else {
        await albumsAPI.create(formData);
      }
      
      setShowAlbumModal(false);
      setAlbumName('');
      setAlbumDescription('');
      setAlbumCover(null);
      setEditingAlbum(null);
      loadAlbums();
    } catch (error) {
      console.error('Erro ao salvar álbum:', error);
      alert('Erro ao salvar álbum. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await authAPI.register(userEmail, userPassword, userRole);
      setShowUserModal(false);
      setUserEmail('');
      setUserPassword('');
      setUserRole('listener');
      alert('Usuário criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      alert('Erro ao criar usuário');
    }
  };

  if (loading) {
    return <LoadingSpinner text="Carregando álbuns..." />;
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>Soulful Roots</Title>
        <SearchBar />
        <UserInfo>
          <span>{user?.email}</span>
          {user?.role === 'admin' && (
            <>
              <button 
                className="btn btn-secondary mobile-hidden"
                onClick={() => setShowUserModal(true)}
              >
                👤 Novo Usuário
              </button>
              <button 
                className="btn btn-secondary mobile-hidden"
                onClick={() => setShowUserManagement(true)}
              >
                👥 Gerenciar
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setEditingAlbum(null);
                  setAlbumName('');
                  setAlbumDescription('');
                  setAlbumCover(null);
                  setShowAlbumModal(true);
                }}
              >
                ➕ Novo Álbum
              </button>
            </>
          )}
          <div className="mobile-hidden">
            <ThemeToggle />
          </div>
          <button className="btn btn-secondary mobile-hidden" onClick={logout}>
            🚪
          </button>
        </UserInfo>
      </Header>

      <QuickActions>
        <QuickActionCard to="/history">
          <div className="icon">🕒</div>
          <div className="content">
            <div className="title">Histórico</div>
            <div className="subtitle">Tocadas recentemente</div>
          </div>
        </QuickActionCard>
        
        <QuickActionCard to="/favorites">
          <div className="icon">❤️</div>
          <div className="content">
            <div className="title">Favoritas</div>
            <div className="subtitle">Suas músicas preferidas</div>
          </div>
        </QuickActionCard>
      </QuickActions>

      <AlbumsGrid>
        {albums.map(album => (
          <AlbumCard key={album.id} to={`/album/${album.id}`}>
            <AlbumCardContent>
              <div style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 1, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <StatusBadge released={!!album.is_released}>
                  {album.is_released ? '✅' : '🕐'}
                  {album.is_released ? 'Lançado' : 'Produção'}
                </StatusBadge>
                {!!album.is_hidden && (
                  <StatusBadge released={false} style={{ background: '#666' }}>
                    👁️ Oculto
                  </StatusBadge>
                )}
              </div>
              
              <AlbumCover image={album.cover_image} />
              
              <AlbumInfo>
                <h3>{album.name}</h3>
                {album.description && <p>{album.description}</p>}
                <SongCount>
                  🎵
                  {album.song_count} música{album.song_count !== 1 ? 's' : ''}
                </SongCount>
                {user?.role === 'admin' && (
                  <div style={{ marginTop: 'auto', paddingTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }} className="desktop-only">
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '12px' }}
                      onClick={(e) => {
                        e.preventDefault();
                        setEditingAlbum(album);
                        setShowAlbumModal(true);
                      }}
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '12px' }}
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          await albumsAPI.updateVisibility(album.id, !album.is_hidden);
                          loadAlbums();
                        } catch (error) {
                          console.error('Erro ao alterar visibilidade:', error);
                        }
                      }}
                    >
                      {album.is_hidden ? '👁️' : '🙈'}
                    </button>
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '12px' }}
                      onClick={async (e) => {
                        e.preventDefault();
                        if (window.confirm(`Tem certeza que deseja excluir o álbum "${album.name}"? Esta ação não pode ser desfeita.`)) {
                          try {
                            await albumsAPI.delete(album.id);
                            loadAlbums();
                          } catch (error) {
                            console.error('Erro ao excluir álbum:', error);
                            alert('Erro ao excluir álbum');
                          }
                        }
                      }}
                      title="Excluir Álbum"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </AlbumInfo>
            </AlbumCardContent>
          </AlbumCard>
        ))}
      </AlbumsGrid>

      {/* Modal Novo Álbum */}
      <Modal show={showAlbumModal}>
        <ModalContent>
          <h2>{editingAlbum ? 'Editar Álbum' : 'Novo Álbum'}</h2>
          <Form onSubmit={handleCreateAlbum}>
            <Input
              type="text"
              placeholder="Nome do álbum"
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              required
            />
            <TextArea
              placeholder="Descrição (opcional)"
              value={albumDescription}
              onChange={(e) => setAlbumDescription(e.target.value)}
            />
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const maxSize = 10 * 1024 * 1024; // 10MB
                  if (file.size > maxSize) {
                    alert('Imagem muito grande! Máximo 10MB.');
                    e.target.value = '';
                    return;
                  }
                }
                setAlbumCover(file || null);
              }}
            />
            {albumCover && (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                🖼️ {albumCover.name} ({(albumCover.size / 1024 / 1024).toFixed(1)}MB)
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading ? '⏳ Salvando...' : (editingAlbum ? 'Atualizar' : 'Criar')}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAlbumModal(false);
                  setEditingAlbum(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </Form>
        </ModalContent>
      </Modal>

      {/* Modal Novo Usuário */}
      <Modal show={showUserModal}>
        <ModalContent>
          <h2>Novo Usuário</h2>
          <Form onSubmit={handleCreateUser}>
            <Input
              type="email"
              placeholder="Email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              required
            />
            <Input
              type="password"
              placeholder="Senha"
              value={userPassword}
              onChange={(e) => setUserPassword(e.target.value)}
              required
            />
            <select 
              value={userRole} 
              onChange={(e) => setUserRole(e.target.value)}
              style={{
                padding: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            >
              <option value="listener">Ouvinte</option>
              <option value="admin">Administrador</option>
            </select>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary">Criar</button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowUserModal(false)}
              >
                Cancelar
              </button>
            </div>
          </Form>
        </ModalContent>
      </Modal>

      {/* Gerenciamento de Usuários */}
      <UserManagement 
        show={showUserManagement} 
        onClose={() => setShowUserManagement(false)} 
      />
    </DashboardContainer>
  );
};

export default Dashboard;